import {
  soloGetStatus, soloMakeMove, soloGetHint, soloSetNotes, soloDeleteNotes, soloUndoMove, soloEraseCell, soloSubmitSolution,
  soloAbandonPuzzle, soloRejectPuzzle, soloStartPuzzle, soloFindPuzzle,
  duelGetStatus, duelMakeMove, duelGetHint, duelSetNotes, duelDeleteNotes, duelUndoMove, duelEraseCell, duelSubmitSolution,
  duelAbandonPuzzle, duelRejectPuzzle, duelStartPuzzle, duelFindPuzzle, duelFindByChallenge,
  mirrorGetStatus, mirrorMakeMove, mirrorGetHint, mirrorSetNotes, mirrorDeleteNotes, mirrorUndoMove, mirrorEraseCell, mirrorSubmitSolution,
  mirrorAbandonPuzzle, mirrorRejectPuzzle, mirrorStartPuzzle, mirrorFindPuzzle,
  tournamentGetStatus, tournamentMakeMove, tournamentGetHint, tournamentSetNotes, tournamentDeleteNotes, tournamentUndoMove, tournamentEraseCell, tournamentSubmitSolution,
  tournamentAbandonPuzzle, tournamentRejectPuzzle, tournamentStartPuzzle, tournamentFindPuzzle, tournamentJoin, tournamentGetTournaments, tournamentGetDetail,
  createGetStatus, createMakeMove, createGetHint, createSetNotes, createDeleteNotes, createUndoMove, createEraseCell, createSubmitSolution,
  createAbandonPuzzle, createRejectPuzzle, createStartPuzzle, createFindPuzzle, createValidate, createReveal, createOperation, createDelete,
  createColor, createSelect
} from './api';

const dispatchByMode = {
  getStatus: {
    solo: soloGetStatus,
    duel: duelGetStatus,
    mirror: mirrorGetStatus,
    tournament: tournamentGetStatus,
    create: createGetStatus,
  },
  findPuzzle: {
    solo: soloFindPuzzle,
    duel: duelFindPuzzle,
    mirror: mirrorFindPuzzle,
    create: createFindPuzzle,
  },
  findPuzzleByChallenge: {
    duel: duelFindByChallenge,
  },
  startPuzzle: {
    solo: soloStartPuzzle,
    duel: duelStartPuzzle,
    mirror: mirrorStartPuzzle,
    tournament: tournamentStartPuzzle,
    create: createStartPuzzle,
  },
  makeMove: {
    solo: soloMakeMove,
    duel: duelMakeMove,
    mirror: mirrorMakeMove,
    tournament: tournamentMakeMove,
    create: createMakeMove,
  },
  getHint: {
    solo: soloGetHint,
    duel: duelGetHint,
    mirror: mirrorGetHint,
    tournament: tournamentGetHint,
    create: createGetHint,
  },
  setNotes: {
    solo: soloSetNotes,
    duel: duelSetNotes,
    mirror: mirrorSetNotes,
    tournament: tournamentSetNotes,
    create: createSetNotes,
  },
  deleteNotes: {
    solo: soloDeleteNotes,
    duel: duelDeleteNotes,
    mirror: mirrorDeleteNotes,
    tournament: tournamentDeleteNotes,
    create: createDeleteNotes,
  },
  undoMove: {
    solo: soloUndoMove,
    duel: duelUndoMove,
    mirror: mirrorUndoMove,
    tournament: tournamentUndoMove,
    create: createUndoMove,
  },
  eraseCell: {
    solo: soloEraseCell,
    duel: duelEraseCell,
    mirror: mirrorEraseCell,
    tournament: tournamentEraseCell,
    create: createEraseCell,
  },
  submitSolution: {
    solo: soloSubmitSolution,
    duel: duelSubmitSolution,
    mirror: mirrorSubmitSolution,
    tournament: tournamentSubmitSolution,
    create: createSubmitSolution,
  },
  abandonPuzzle: {
    solo: soloAbandonPuzzle,
    duel: duelAbandonPuzzle,
    mirror: mirrorAbandonPuzzle,
    tournament: tournamentAbandonPuzzle,
    create: createAbandonPuzzle,
  },
  rejectPuzzle: {
    solo: soloRejectPuzzle,
    duel: duelRejectPuzzle,
    mirror: mirrorRejectPuzzle,
    tournament: tournamentRejectPuzzle,
    create: createRejectPuzzle,
  },
};

const modeGuard = (map, mode, action) => {
  const fn = map?.[mode];
  if (!fn) throw new Error(`Unsupported mode '${mode}' for action '${action}'`);
  return fn;
};

export const findPuzzle = (mode) => {
  const fn = dispatchByMode.findPuzzle[mode];
  if (!fn) throw new Error(`No findPuzzle implementation for mode: ${mode}`);
  return fn();
};

export const findPuzzleByChallenge = (mode, challengeCode) => {
  return modeGuard(dispatchByMode.findPuzzleByChallenge, mode, 'findPuzzleByChallenge')(challengeCode);
};

export const getModeStatus = (mode) => modeGuard(dispatchByMode.getStatus, mode, 'getStatus')();
export const startPuzzle = (mode) => modeGuard(dispatchByMode.startPuzzle, mode, 'startPuzzle')();
export const makeMove = (mode, x, y, value) => modeGuard(dispatchByMode.makeMove, mode, 'makeMove')(x, y, value);
export const getHint = (mode) => modeGuard(dispatchByMode.getHint, mode, 'getHint')();
export const setNotes = (mode, x, y, value) => modeGuard(dispatchByMode.setNotes, mode, 'setNotes')(x, y, value);
export const deleteNotes = (mode, x, y) => modeGuard(dispatchByMode.deleteNotes, mode, 'deleteNotes')(x, y);
export const undoMove = (mode) => modeGuard(dispatchByMode.undoMove, mode, 'undoMove')();
export const eraseCell = (mode, x, y) => modeGuard(dispatchByMode.eraseCell, mode, 'eraseCell')(x, y);
export const submitSolution = (mode) => modeGuard(dispatchByMode.submitSolution, mode, 'submitSolution')();
export const abandonPuzzle = (mode) => modeGuard(dispatchByMode.abandonPuzzle, mode, 'abandonPuzzle')();
export const rejectPuzzle = (mode) => modeGuard(dispatchByMode.rejectPuzzle, mode, 'rejectPuzzle')();

export const joinTournamentByShareToken = (shareToken) => tournamentJoin(shareToken);
export { tournamentFindPuzzle, tournamentGetTournaments, tournamentGetDetail };

// CREATE-SPECIFIC FUNCTIONS
export const validatePuzzle = (mode) => {
  if (mode !== 'create') throw new Error(`Validate only supported for create mode`);
  return createValidate();
};

export const revealCell = (mode) => {
  if (mode !== 'create') throw new Error(`Reveal only supported for create mode`);
  return createReveal();
};

export const operationCell = (mode, operation) => {
  if (mode !== 'create') throw new Error(`Operation only supported for create mode`);
  return createOperation(operation);
};

export const deleteCell = (mode) => {
  if (mode !== 'create') throw new Error(`Delete only supported for create mode`);
  return createDelete();
};

export const colorCell = (mode, color) => {
  if (mode !== 'create') throw new Error(`Color only supported for create mode`);
  return createColor(color);
};

export const selectCell = (mode, x, y) => {
  if (mode !== 'create') throw new Error(`Select only supported for create mode`);
  return createSelect(x, y);
};
